import React from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import {
  Container,
  PaginationEllipsis,
  PaginationLinks,
  textStyles,
} from '@marketing-sites/ui'
import Head from 'next/head'
import type { ArticlesQueryQuery } from '../../../.graphql/generated-types'
import { ArticlesQueryDocument } from '../../../.graphql/generated-types'
import {
  notEmpty,
  calculatePageCount,
  extractPageNumberFromParam,
  pageSize,
  siteLocale,
} from '@marketing-sites/utils'
import { getGraphQLClient, graphQLClient } from '../../lib/api'
import { ArticleListItem } from '../ArticleListItem'
import { FeaturedArticleListItem } from '../../components/FeaturedArticleListItem'
import { ComposeComponentTopicList } from '../ComposeComponentTopicList'

import {
  ArticlePaginationLink,
  ArticlePaginationNextLink,
  ArticlePaginationPrevLink,
} from '../ArticlePagination'
import type { Substitutions } from '../../components/ContentfulRichText'
import { fetchGlobalRegionalEditsAsSubstitutions } from '../GlobalRegionalEdits'

export interface ArticleIndexPageProps {
  kind: 'ArticleIndexPageProps'
  data?: ArticlesQueryQuery | null
  substitutions: Substitutions
  pageNumber: number
}

export function ArticlesIndexPage({
  data,
  substitutions,
  pageNumber,
}: ArticleIndexPageProps) {
  const firstPublishedAtDesc = data?.composePageArticleCollection?.items
    .filter(notEmpty)
    .sort((a, b) => {
      const sysA = a.sys.firstPublishedAt as string | null | undefined
      const sysB = b.sys.firstPublishedAt as string | null | undefined

      if (!sysA || !sysB) return 0
      return new Date(sysB).getTime() - new Date(sysA).getTime()
    })

  const latestArticles = firstPublishedAtDesc?.map((articleListItemData) => (
    <li
      key={articleListItemData.sys.id}
      className="bg-white hover:shadow-[0px_0px_10px_rgba(0,0,0,0.16)]"
    >
      <ArticleListItem
        data={articleListItemData}
        substitutions={substitutions}
      />{' '}
    </li>
  ))

  const total = data?.composePageArticleCollection?.total ?? 1
  const limit = data?.composePageArticleCollection?.limit ?? 1
  const pageCount = Math.ceil(total / limit)

  const [featuredArticle] =
    data?.composeComponentFeaturedArticleCollection?.items
      .filter(notEmpty)
      .map((articleListItemData) => articleListItemData.featuredArticle)
      .filter(notEmpty)
      .map((articleListItemData) => (
        <li key={articleListItemData.sys.id}>
          <FeaturedArticleListItem
            data={articleListItemData}
            substitutions={substitutions}
          />{' '}
        </li>
      )) ?? []

  const topicList = data?.composeComponentTopicListCollection?.items[0]

  const popularArticles =
    data?.composeComponentPopularArticlesCollection?.items[0]?.articlesCollection?.items
      .filter(notEmpty)
      .map((articleListItemData) => (
        <li
          key={articleListItemData.sys.id}
          className="bg-white hover:shadow-[0px_0px_10px_rgba(0,0,0,0.16)]"
        >
          <ArticleListItem
            data={articleListItemData}
            substitutions={substitutions}
          />{' '}
        </li>
      )) ?? []

  let title = 'Reading Eggs Articles'
  const metaDescription =
    data?.composePageArticlesIndexPageCollection?.items[0]?.seoDescription ??
    'All the latest articles from Reading Eggs.'
  if (pageNumber > 1) title = `${title}, Page ${pageNumber.toString()}`
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
      </Head>
      <Container className="space-y-12 py-12">
        <>
          <h1 className={textStyles.h1}>Reading Eggs Articles</h1>
          {featuredArticle && <ul className="space-y-12">{featuredArticle}</ul>}

          <h2 className={textStyles.h2}>Latest Articles</h2>

          <ul className="space-y-12">{latestArticles}</ul>
          <div className="flex justify-center">
            <PaginationLinks
              pageCount={pageCount}
              currentPage={pageNumber}
              PaginationLinkComponent={ArticlePaginationLink}
              EllipsisComponent={PaginationEllipsis}
              PrevPageLinkComponent={ArticlePaginationPrevLink}
              NextPageLinkComponent={ArticlePaginationNextLink}
            />
          </div>
        </>
      </Container>
      <div className="bg-gray">
        {topicList && (
          <div className="mb-12 bg-slate-50 py-16">
            <Container>
              <>
                <h2 className={`${textStyles.h2} mb-6 self-start`}>
                  Discover Topics
                </h2>
                <ComposeComponentTopicList data={topicList} />
              </>
            </Container>
          </div>
        )}
      </div>
      {popularArticles.length > 0 && (
        <Container className="mb-24 space-y-12">
          <>
            <h2 className={textStyles.h2}>Popular Articles</h2>

            <ul className="space-y-12">{popularArticles}</ul>
          </>
        </Container>
      )}
    </>
  )
}

export const getStaticPathsForArticleIndexPage: GetStaticPaths = async () => {
  const result = await graphQLClient
    .query(ArticlesQueryDocument, {
      limit: 1,
      skip: 0,
      preview: false,
      locale: siteLocale,
    })
    .toPromise()
  const itemCount = result.data?.composePageArticleCollection?.total ?? 1
  const pageCount = calculatePageCount(itemCount, pageSize)
  const paths = Array.from({ length: pageCount }, (_x, i) => i + 1).map(
    (pageNumber) => ({
      params: { slug: `page-${pageNumber.toString()}` },
    }),
  )
  return { paths, fallback: false }
}

export const getStaticPropsForArticleIndexPage: GetStaticProps<
  ArticleIndexPageProps
> = async ({ params, preview }) => {
  let pageNumberParam = 1
  const slug = params?.['slug']
  if (typeof slug === 'string') {
    const pageNumberFromSlug = extractPageNumberFromParam(slug)
    if (typeof pageNumberFromSlug === 'number') {
      pageNumberParam = pageNumberFromSlug
    }
  }

  const skip = pageSize * (pageNumberParam - 1)
  const limit = pageSize

  const result = await getGraphQLClient(preview)
    .query(ArticlesQueryDocument, {
      limit,
      skip,
      preview: !!preview,
      locale: siteLocale,
    })
    .toPromise()

  const substitutions: Substitutions =
    await fetchGlobalRegionalEditsAsSubstitutions()

  if (typeof result.data === 'undefined') {
    return {
      props: {
        kind: 'ArticleIndexPageProps',
        data: null,
        substitutions,
        pageNumber: pageNumberParam,
      },
    }
  }

  return {
    props: {
      kind: 'ArticleIndexPageProps',
      data: result.data,
      substitutions,
      pageNumber: pageNumberParam,
    },
  }
}
